import { FieldArrayPathValue, PathValue } from 'react-hook-form';
import { SetRequired } from 'type-fest';

import { MatcherSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { ArrayFieldWithID } from '@/modules/aiSuggestions/AISuggestionsMatcher/hooks/useUpdateSubformArrayField';
import { BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE } from '@/modules/beneficiaryDesignations/BeneficiaryDesignationsForm.types';
import { AddNewAnythingModalProps } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import { DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import {
  BASIC_INFORMATION_SUBFORM_NAMESPACE,
  Fields as BasicInformationSubformFields,
} from '@/modules/entities/BasicInformationSubform/BasicInformationSubform.types';
import { BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import { CombinedSupportedSubformFieldTypes } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import {
  BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE,
  BENEFICIARIES_SUBFORM,
  LIFETIME_BENEFICIARIES_SUBFORM,
} from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { TRUST_DETAILS_SUBFORM_NAMESPACE } from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.types';
import { AiSuggestionKind } from '@/types/schema';

export enum AISuggestionsMatcherVariant {
  GRANTORS = 'GRANTORS',
  // Only allows matching one grantor, for things like SLAT's
  SINGLE_GRANTOR = 'SINGLE_GRANTOR',
  // Includes trustees, successor trustees, and trust advisors
  TRUSTEES = 'TRUSTEES',
  BENEFICIARIES_V2 = 'BENEFICIARIESV2',
  BUSINESS_PEOPLE = 'BUSINESS_PEOPLE', // Includes beneficial owners and key people
  DP_RECIPIENTS_GRANTOR_1_DIES_FIRST = 'DP_RECIPIENTS_GRANTOR_1_DIES_FIRST',
  DP_RECIPIENTS_GRANTOR_2_DIES_FIRST = 'DP_RECIPIENTS_GRANTOR_2_DIES_FIRST',
  DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH = 'DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH',
  DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH = 'DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH',
  DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH = 'DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH',
  DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH = 'DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH',
  TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST = 'TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST',
  TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST = 'TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST',
  TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH = 'TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_FIRST_DEATH',
  TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH = 'TE_DP_RECIPIENTS_GRANTOR_1_DIES_FIRST_SECOND_DEATH',
  TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH = 'TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_FIRST_DEATH',
  TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH = 'TE_DP_RECIPIENTS_GRANTOR_2_DIES_FIRST_SECOND_DEATH',
  // Subtrust beneficiaries - for testamentary entity forms
  SUBTRUST_LIFETIME_BENEFICIARIES = 'SUBTRUST_LIFETIME_BENEFICIARIES',
  SUBTRUST_REMAINDER_BENEFICIARIES = 'SUBTRUST_REMAINDER_BENEFICIARIES',
  // Subtrust trustees - includes trustees, successor trustees, and trust advisors
  SUBTRUST_TRUSTEES = 'SUBTRUST_TRUSTEES',
  // Beneficiary designation recipients (retirement / insurance accounts). The
  // same DP_RECIPIENT_V2 suggestions are split into the editor's primary and
  // contingent sections by the suggestion's dpBeneficiaryDesignationTier.
  BENEFICIARY_DESIGNATION_RECIPIENTS_PRIMARY = 'BENEFICIARY_DESIGNATION_RECIPIENTS_PRIMARY',
  BENEFICIARY_DESIGNATION_RECIPIENTS_CONTINGENT = 'BENEFICIARY_DESIGNATION_RECIPIENTS_CONTINGENT',
}

// These are the full field paths to the subform array fields that we allow
// AI suggestions to match against.
export const AI_SUGGESTIONS_MATCHER_ARRAY_FIELD_PATHS = [
  `${BASIC_INFORMATION_SUBFORM_NAMESPACE}.grantors`,
  `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustees`,
  `${TRUST_DETAILS_SUBFORM_NAMESPACE}.successorTrustees`,
  `${TRUST_DETAILS_SUBFORM_NAMESPACE}.trustAdvisors`,
  `${BENEFICIARIES_SUBFORM}.beneficiaries`,
  `${LIFETIME_BENEFICIARIES_SUBFORM}.lifetimeBeneficiaries`,
  `${BENEFICIARIES_AFTER_BOTH_GRANTORS_DIE}.beneficiaries`,
  `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.owners`,
  `${BUSINESS_ENTITY_DETAILS_SUBFORM_NAMESPACE}.keyPeople`,
  `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients`,
  `${DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.firstDeath.recipients`,
  `${DISPOSITIVE_PROVISIONS_FORM_V2_NAMESPACE}.secondDeath.recipients`,
  `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.primary.recipients`,
  `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.contingent.recipients`,
] as const;
export type AISuggestionsMatcherArrayField =
  (typeof AI_SUGGESTIONS_MATCHER_ARRAY_FIELD_PATHS)[number];

/**
 * This represents one element of one of the subform array fields.
 * For example, one trustee.
 */
export type ArrayFieldElement = NonNullable<
  FieldArrayPathValue<
    CombinedSupportedSubformFieldTypes,
    AISuggestionsMatcherArrayField
  >
>[number];

// These are the full field paths to the subform single fields that we allow
// AI suggestions to match against.
export const AI_SUGGESTIONS_MATCHER_SINGLE_FIELD_PATHS = [
  `${BASIC_INFORMATION_SUBFORM_NAMESPACE}.grantor`,
] as const;
export type AISuggestionsMatcherSingleField =
  (typeof AI_SUGGESTIONS_MATCHER_SINGLE_FIELD_PATHS)[number];

export type AISuggestionsMatcherAddNewAnythingModalProps = SetRequired<
  Pick<
    AddNewAnythingModalProps,
    | 'addableTypes'
    | 'addClientProfileModalProps'
    | 'addClientOrganizationModalProps'
    | 'addEntityModalProps'
    | 'addTestamentaryEntityModalProps'
  >,
  | 'addableTypes'
  | 'addClientProfileModalProps'
  | 'addClientOrganizationModalProps'
  | 'addEntityModalProps'
  // note that addTestamentaryEntityModalProps isn't marked as required here,
  // because it's only available as an option for one AI suggestions matcher,
  // so it's simpler to only configure those properties when needed
>;

export interface AISuggestionsMatcherVariantConfig {
  nounSingular: string;
  nounPlural: string;
  calloutText?: string;
  suggestionKinds: AiSuggestionKind[];
  addNewAnythingModalProps: Partial<
    Record<AiSuggestionKind, AISuggestionsMatcherAddNewAnythingModalProps>
  >;
  subformArrayFieldUpdateProps: (ArrayFieldWithID & {
    mapperFunc: (
      matches: AISuggestionMatch[]
    ) => FieldArrayPathValue<
      CombinedSupportedSubformFieldTypes,
      AISuggestionsMatcherArrayField
    >;
  })[];
  subformArrayFieldOnConflict: 'overwrite' | 'append';
  subformSingleFieldUpdateProps?: {
    singleFieldFullPath: AISuggestionsMatcherSingleField;
    mapperFunc: (
      match: AISuggestionMatch[]
    ) => PathValue<
      BasicInformationSubformFields,
      AISuggestionsMatcherSingleField
    >;
  };
  /**
   * If this is true, we'll still show the commit button even if there are no
   * suggestions to show. This is used if the user selects "none" for the
   * death event suggestion picker, where we hide the suggested recipients, but
   * still want the user to be able to commit the "none" selection.
   */
  showCommitButtonOnEmptySuggestions?: boolean;
}

export interface SuggestionsGroup {
  // If not given, we won't show the header text for the list of suggestions
  groupLabel?: string;
  suggestions: MatcherSuggestion[];
}

export interface AISuggestionMatch {
  clientProfileId?: string;
  clientOrganizationId?: string;
  entityId?: string;
  testamentaryId?: string;
  // Set to true when the match represents the "surviving spouse" sentinel
  // (i.e. the spouse who survives, not a specific client profile). Only
  // applicable to subtrust beneficiary suggestions in two-grantor households.
  survivingSpouse?: boolean;
  suggestion: MatcherSuggestion;
}

export interface MatchableField {
  isSuggestion?: boolean;
}
